import React, { useState } from "react";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import moment from "moment";
import SelectWrapper from "../../../SelectWrapper";
import Loader from "../../../Loader";
import InformationModal from "../../../InformationModal";
import { customersApi } from "../../../../services/customerServices";
import { externalApiKeysApi } from "../../../../services/externalApiKeysServices";
import { pickPlaintextKeyFromCreateResponse } from "../apiKeyPayloadUtils";

const PAGE_SIZE = 30;

const CONFIG_JSON_PLACEHOLDER = `{
  "rateLimit": {
    "max": 500,
    "window": "1h"
  }
}`;

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

const configToText = (config) => {
  if (config == null) return "";
  if (typeof config === "string") return config;
  try {
    return JSON.stringify(config, null, 2);
  } catch {
    return "";
  }
};

const expiresAtToLocal = (iso) => {
  if (!iso) return "";
  const m = moment(iso);
  return m.isValid() ? m.format("YYYY-MM-DDTHH:mm") : "";
};

/** Default and maximum expiration: one calendar year from a reference moment (typically now). */
const oneYearFrom = (m) => m.clone().add(1, "year");

/**
 * Handles both create and edit modes for a customer API key.
 *
 * - Create mode (no `record` prop): shows customer selector, calls onCreated(plaintext).
 *   Expiration defaults to one year from now (editable, max one year ahead).
 * - Edit mode (`record` prop provided): hides customer (cannot change), pre-populates
 *   name / config / expiresAt, calls onUpdated() after a successful PATCH.
 */
const CreateCustomerApiKeyModal = ({
  record,
  onClose,
  onCreated,
  onUpdated,
}) => {
  const isEditing = Boolean(record?.id);

  const [selectedCustomer, setSelectedCustomer] = useState(
    record?.customerId
      ? { id: record.customerId, customerName: record.customerName }
      : undefined
  );
  const [name, setName] = useState(record?.name ?? "");
  const [configText, setConfigText] = useState(() =>
    configToText(record?.config)
  );
  const [expiresAtLocal, setExpiresAtLocal] = useState(() => {
    if (record?.expiresAt) {
      return expiresAtToLocal(record.expiresAt);
    }
    return oneYearFrom(moment()).format("YYYY-MM-DDTHH:mm");
  });
  const [loading, setLoading] = useState(false);
  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
    onClose: null,
  });

  const showMessage = (title, body, onClose = null) =>
    setInformationModal({ isOpen: true, title, body, onClose });

  const showError = (title, body) => showMessage(title, body);

  const parseConfig = () => {
    const t = configText.trim();
    if (!t) {
      return { ok: true, value: undefined };
    }
    let parsed;
    try {
      parsed = JSON.parse(t);
    } catch {
      return { ok: false, message: "Config must be valid JSON object syntax." };
    }
    if (!isPlainObject(parsed)) {
      return {
        ok: false,
        message: "Config must be a JSON object (not an array or scalar).",
      };
    }
    return { ok: true, value: parsed };
  };

  const buildPayload = () => {
    if (!isEditing && !selectedCustomer?.id) {
      showError("Validation", "Please select a customer.");
      return null;
    }
    if (!name.trim()) {
      showError("Validation", "Please enter a name for the key.");
      return null;
    }
    const cfg = parseConfig();
    if (!cfg.ok) {
      showError("Validation", cfg.message);
      return null;
    }

    const payload = { name: name.trim() };

    if (!isEditing) {
      payload.customerId = selectedCustomer.id;
    }
    if (cfg.value !== undefined) {
      payload.config = cfg.value;
    }

    if (!expiresAtLocal?.trim()) {
      showError("Validation", "Please set an expiration date.");
      return null;
    }
    const m = moment(expiresAtLocal);
    if (!m.isValid()) {
      showError("Validation", "Invalid expiration date.");
      return null;
    }
    const now = moment();
    if (m.isSameOrBefore(now)) {
      showError("Validation", "Expiration date must be in the future.");
      return null;
    }
    const latest = oneYearFrom(now);
    if (m.isAfter(latest)) {
      showError(
        "Validation",
        "Expiration cannot be more than one year from now."
      );
      return null;
    }
    payload.expiresAt = m.toISOString();

    return payload;
  };

  const doSubmit = async (e) => {
    e.preventDefault();
    const payload = buildPayload();
    if (!payload) return;

    setLoading(true);
    try {
      if (isEditing) {
        await externalApiKeysApi.update(record.id, payload);
        setLoading(false);
        showMessage(
          "API key updated",
          `The key <strong>${
            record.name || record.id
          }</strong> was updated successfully.`,
          onUpdated
        );
      } else {
        const result = await externalApiKeysApi.create(payload);
        const plaintext = pickPlaintextKeyFromCreateResponse(result);
        setLoading(false);
        if (!plaintext) {
          return showError(
            "Generate Key",
            "The key was created but the response did not include the secret. Check with your administrator."
          );
        }
        onCreated(plaintext);
      }
    } catch (err) {
      setLoading(false);
      const msg =
        err?.response?.data?.[0]?.msg ||
        err?.response?.data?.message ||
        err?.message ||
        (isEditing
          ? "There was an error updating the API key."
          : "There was an error generating the API key.");
      showError(isEditing ? "Update Key" : "Generate Key", msg);
    }
  };

  const closeBtn = (
    <Button className="close" color="none" onClick={onClose}>
      &times;
    </Button>
  );

  const closeInformationModal = () => {
    const cb = informationModal.onClose;
    setInformationModal({ isOpen: false, title: "", body: "", onClose: null });
    if (cb) cb();
  };

  const expiresMin = moment().format("YYYY-MM-DDTHH:mm");
  const expirationCap = oneYearFrom(moment());
  const selectedExpiration = moment(expiresAtLocal);
  const expiresMax = (
    selectedExpiration.isValid() && selectedExpiration.isAfter(expirationCap)
      ? selectedExpiration
      : expirationCap
  ).format("YYYY-MM-DDTHH:mm");

  return informationModal.isOpen ? (
    <InformationModal
      title={informationModal.title}
      body={informationModal.body}
      onClose={closeInformationModal}
    />
  ) : (
    <Modal isOpen={true} size="md" backdrop="static" scrollable>
      <Form onSubmit={doSubmit}>
        <ModalHeader
          className="d-flex justify-content-between"
          close={closeBtn}
        >
          {isEditing
            ? `Edit API key — ${record.name || record.id}`
            : "Create API key (customer)"}
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <Loader size="sm" />
          ) : (
            <Row>
              {!isEditing && (
                <Col sm="12" className="mb-3">
                  <FormGroup>
                    <Label for="customerId">
                      Customer
                      <span className="text-danger ml-1">*</span>
                    </Label>
                    <SelectWrapper
                      isRequired
                      entity="customer"
                      formatItemFunction={(c) => ({
                        label: c.customerName || c.customerNo || `ID ${c.id}`,
                        value: c.id,
                      })}
                      filterItemFunction={(c) => c.customerName || c.customerNo}
                      fetchFunction={customersApi.getCustomers}
                      fetchParameters={{ pageSize: PAGE_SIZE }}
                      defaultSelected={selectedCustomer}
                      onSelected={setSelectedCustomer}
                      name="customerId"
                    />
                  </FormGroup>
                </Col>
              )}
              <Col sm="12" className="mb-3">
                <FormGroup>
                  <Label for="keyName">
                    Name
                    <span className="text-danger ml-1">*</span>
                  </Label>
                  <Input
                    id="keyName"
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Display name for this key"
                    required
                  />
                </FormGroup>
              </Col>
              <Col sm="12" className="mb-3">
                <FormGroup>
                  <Label for="config">Config (JSON object)</Label>
                  <Input
                    id="config"
                    name="config"
                    type="textarea"
                    rows={6}
                    value={configText}
                    onChange={(e) => setConfigText(e.target.value)}
                    placeholder={CONFIG_JSON_PLACEHOLDER}
                    className="text-monospace small"
                  />
                  <div className="text-muted small mt-1">
                    Optional. Must be a single JSON object.
                  </div>
                </FormGroup>
              </Col>
              <Col sm="12" className="mb-0">
                <FormGroup>
                  <Label for="expiresAt">
                    Expires At
                    <span className="text-danger ml-1">*</span>
                  </Label>
                  <Input
                    id="expiresAt"
                    name="expiresAt"
                    type="datetime-local"
                    min={expiresMin}
                    max={expiresMax}
                    value={expiresAtLocal}
                    onChange={(e) => setExpiresAtLocal(e.target.value)}
                    required
                  />
                  <div className="text-muted small mt-1">
                    Defaults to one year from now. You can choose any time up to
                    one year ahead.
                  </div>
                </FormGroup>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Col>
            <Row className="justify-content-between">
              <Button color="secondary" onClick={onClose} type="button">
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={loading}>
                {isEditing ? "Save changes" : "Generate key"}
              </Button>
            </Row>
          </Col>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CreateCustomerApiKeyModal;
